import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  PagedResultDto,
  ShiftRequestSearchDto
} from '@umss/shared/data-access';
import {
  DatePeriod,
  HttpHelper,
  ScopeOfPracticeEnum,
  ShiftRequestStatusEnum,
  ShiftTypeEnum,
  formatDateOnly,
  formatShortDate,
  getDayOfWeek,
  period2DateRange
} from '@umss/shared/utils';
import dayjs from 'dayjs';
import { forkJoin, Observable, catchError, map, of, switchMap } from 'rxjs';
import {
  DashboardDataDto,
  DashboardShiftRequestDto,
  PayPeriodStatsDto,
  UpcomingShiftDto,
  UrgentShiftDto
} from '../dtos';
import { ShiftNeedsService } from './shift-needs.service';
import {
  PayPeriodStats,
  ShiftRequest,
  UpcomingShift,
  UrgentShift
} from '../models';
import { buildShiftNeedSearchDto } from '../utils/shift-search-builder';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private readonly http = inject(HttpClient);
  private readonly shiftNeedsService = inject(ShiftNeedsService);

  private readonly shiftsApi = '/api/shifts';
  private readonly shiftNeedsApi = '/api/shift-needs';
  private readonly contractorsApi = '/api/contractors';

  /**
   * Get upcoming shifts for the current contractor
   * Falls back to mock data if API fails
   */
  getUpcomingShifts(
    daysAhead = 7,
    startDate?: string,
    endDate?: string
  ): Observable<UpcomingShiftDto[]> {
    const params: any = {};

    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    } else {
      params.daysAhead = daysAhead.toString();
    }

    return this.http.get<UpcomingShiftDto[]>(`${this.shiftsApi}/upcoming`, {
      params
    });
  }

  /**
   * Get confirmed shifts for the current contractor using /me/shifts endpoint
   * Same endpoint as Schedule page - returns paginated Shift records
   */
  getMyShifts(
    startDate?: string,
    endDate?: string,
    pageSize = 100
  ): Observable<PagedResultDto<UpcomingShiftDto>> {
    const params = HttpHelper.toHttpParams({
      startDate,
      endDate,
      pageSize: pageSize.toString()
    });

    return this.http.get<PagedResultDto<UpcomingShiftDto>>(
      `${this.contractorsApi}/me/shifts`,
      { params }
    );
  }

  /**
   * Get all confirmed shifts for the current contractor within a date range
   * Handles pagination automatically to fetch all shifts (max 100 per page)
   * @param startDate Optional start date filter (YYYY-MM-DD)
   * @param endDate Optional end date filter (YYYY-MM-DD)
   * @returns Observable of confirmed shift DTOs (unpaginated array)
   */
  getAllMyShifts(
    startDate?: string,
    endDate?: string
  ): Observable<UpcomingShiftDto[]> {
    // Only pass dates if both are defined to avoid backend 400 error
    const validStartDate = startDate && endDate ? startDate : undefined;
    const validEndDate = startDate && endDate ? endDate : undefined;

    // Use pageSize=100 (backend max) and fetch first page
    return this.getMyShifts(validStartDate, validEndDate, 100).pipe(
      switchMap((firstPage) => {
        const allShifts = [...firstPage.items];

        // If no more pages, return what we have
        if (!firstPage.hasNextPage) {
          return of(allShifts);
        }

        // Fetch remaining pages in parallel
        const pageRequests: Observable<PagedResultDto<UpcomingShiftDto>>[] = [];
        for (let page = 2; page <= firstPage.totalPages; page++) {
          const params = HttpHelper.toHttpParams({
            startDate: validStartDate,
            endDate: validEndDate,
            pageSize: '100',
            pageNumber: page.toString()
          });

          pageRequests.push(
            this.http.get<PagedResultDto<UpcomingShiftDto>>(
              `${this.contractorsApi}/me/shifts`,
              { params }
            )
          );
        }

        // Combine all pages
        return forkJoin(pageRequests).pipe(
          map((pages) => {
            pages.forEach((page) => allShifts.push(...page.items));
            return allShifts;
          })
        );
      })
    );
  }

  /**
   * Get facility-initiated shift requests for the current contractor
   * Delegates to ShiftNeedsService
   * @deprecated Use ShiftNeedsService.getShiftRequests() directly
   */
  getShiftRequests(
    searchDto?: ShiftRequestSearchDto
  ): Observable<DashboardShiftRequestDto[]> {
    return this.shiftNeedsService.getShiftRequests(searchDto);
  }

  /**
   * Get pay period statistics from the new backend endpoint
   * Falls back to mock data if API fails
   */
  getPayPeriodStats(
    periodType: DatePeriod = 'weekly',
    baseDate?: Date
  ): Observable<PayPeriodStatsDto> {
    const currentWeekStart = period2DateRange(periodType, baseDate).start;
    const startDate = formatDateOnly(currentWeekStart);

    const params = HttpHelper.toHttpParams({
      periodType,
      startDate
    });

    return this.http.get<PayPeriodStatsDto>(
      `${this.contractorsApi}/me/pay-period-stats`,
      {
        params
      }
    );
  }

  /**
   * Get urgent shifts (today and future only)
   * Falls back to mock data if API fails
   */
  getUrgentShifts(
    scopeOfPractice?: ScopeOfPracticeEnum
  ): Observable<UrgentShiftDto[]> {
    const today = formatDateOnly(new Date());
    const searchDto = buildShiftNeedSearchDto({
      shiftTypes: [ShiftTypeEnum.Urgent],
      scopesOfPractice: scopeOfPractice ? [scopeOfPractice] : undefined,
      startDate: today,
      excludeExpired: true,
      excludeConflictsForContractor: true,
      excludeStarted: true
    });

    const params = HttpHelper.toHttpParams(searchDto);

    return this.http
      .get<PagedResultDto<UrgentShiftDto>>(this.shiftNeedsApi, { params })
      .pipe(map((result) => result.items));
  }

  /**
   * Get all dashboard data in a single call using forkJoin
   * Returns DTOs that need to be mapped to view models
   */
  getDashboardData(
    scopeOfPractice?: ScopeOfPracticeEnum
  ): Observable<DashboardDataDto> {
    return forkJoin({
      upcomingShifts: this.getUpcomingShifts(),
      shiftRequests: this.getShiftRequests({
        statuses: [ShiftRequestStatusEnum.Pending],
        excludeStarted: true
      }),
      payPeriodStats: this.getPayPeriodStats(),
      urgentShifts: this.getUrgentShifts(scopeOfPractice),
      contractorApplications: this.shiftNeedsService.getMyApplications().pipe(
        map((result) => result.items),
        catchError(() => of([]))
      )
    });
  }

  // ============================================================================
  // MAPPERS: DTO -> View Model
  // ============================================================================

  /**
   * Map UpcomingShiftDto to UpcomingShift view model
   */
  mapToUpcomingShift(dto: UpcomingShiftDto): UpcomingShift {
    return {
      id: dto.id,
      facilityId: dto.facilityId,
      facilityName: dto.facilityName,
      facilityFullAddress: dto.facilityFullAddress,
      facilitySubtypeName: dto.facilitySubtypeName,
      distanceMiles: dto.distanceMiles,
      contractorId: dto.contractorId,
      contractorName: dto.contractorName,
      scopeOfPractice: dto.scopeOfPractice,
      scopeOfPracticeDisplay: dto.scopeOfPracticeDisplay,
      shiftType: dto.shiftType,
      shiftTypeDisplay: dto.shiftTypeDisplay,
      shiftDate: dto.shiftDate,
      startTime: dto.startTime,
      endTime: dto.endTime,
      hourlyRate: dto.hourlyRate,
      specialInstructions: dto.specialInstructions,
      status: dto.status,
      statusDisplay: dto.statusDisplay,
      confirmedAt: dto.confirmedAt,
      confirmedByName: dto.confirmedByName,
      estimatedHours: dto.estimatedHours,
      estimatedPayment: dto.estimatedPayment
    };
  }

  /**
   * Map DashboardShiftRequestDto to ShiftRequest view model
   */
  mapToShiftRequest(dto: DashboardShiftRequestDto): ShiftRequest {
    const estimatedHours = this.calculateEstimatedHours(
      dto.startTime ?? '',
      dto.endTime ?? ''
    );

    const overtimeRate =
      dto.latestCounterOfferRate ??
      dto.rateInfo?.contractorUrgentHourlyRate ??
      dto.rateInfo?.contractorNormalHourlyRate ??
      dto.shiftNeedHourlyRate ??
      0;

    const hourlyRate =
      dto.latestCounterOfferRate ??
      dto.rateInfo?.contractorUrgentHourlyRate ??
      dto.rateInfo?.contractorNormalHourlyRate ??
      dto.shiftNeedHourlyRate ??
      0;

    const estimatedPayment = dto.estimatedPayment ?? 0;

    const shiftDate = dto.shiftDate ?? dayjs().format('YYYY-MM-DD');

    return {
      id: dto.id,
      facilityName: dto.facilityName ?? '',
      location: dto.facilityFullAddress ?? '',
      date: formatShortDate(shiftDate),
      dayOfWeek: getDayOfWeek(shiftDate),
      startTime: dto.startTime ?? '',
      endTime: dto.endTime ?? '',
      shiftNeedId: dto.shiftNeedId,
      facilityId: dto.facilityId ?? '',
      facilitySubtypeName: dto.facilitySubtypeName,
      distanceMiles: dto.distanceMiles,
      scopeOfPractice: dto.scopeOfPractice ?? 0,
      scopeOfPracticeDisplay: dto.scopeOfPracticeDisplay ?? '',
      hourlyRate: overtimeRate ?? hourlyRate,
      latestCounterOfferRate: dto.latestCounterOfferRate,
      estimatedHours,
      estimatedPayment,
      status: dto.status,
      shiftDate
    };
  }

  /**
   * Calculate estimated hours from start and end time
   */
  private calculateEstimatedHours(startTime: string, endTime: string): number {
    if (!startTime || !endTime) return 0;

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let hours = endHour - startHour;
    const minutes = endMin - startMin;

    // Handle overnighy availabilitys
    if (hours < 0) hours += 24;

    return hours + minutes / 60;
  }

  /**
   * Map UrgentShiftDto to UrgentShift view model
   */
  mapToUrgentShift(dto: UrgentShiftDto): UrgentShift {
    const facilityLocation = dto.facilityLocation
      ? `${dto.facilityLocation.city}, ${dto.facilityLocation.stateCode}`
      : undefined;

    const facilityFullAddress =
      dto.facilityFullAddress || dto.facilityLocation?.fullAddress || undefined;

    return {
      id: dto.id,
      facilityId: dto.facilityId,
      facilityName: dto.facilityName,
      facilityLocation,
      facilityFullAddress,
      distanceMiles: dto.distanceMiles,
      facilitySubtypeName: dto.facilitySubtypeName,
      scopeOfPractice: dto.scopeOfPractice,
      scopeOfPracticeDisplay: dto.scopeOfPracticeDisplay,
      shiftDate: dto.shiftDate,
      date: `${getDayOfWeek(dto.shiftDate)}, ${formatShortDate(dto.shiftDate)}`,
      startTime: dto.startTime,
      endTime: dto.endTime,
      hourlyRate:
        dto.rateInfo?.contractorNormalHourlyRate ?? dto.hourlyRate ?? 0,
      percentage: dto.percentageIncrease
        ? `${dto.percentageIncrease}% higher rate`
        : '',
      message: 'Urgent shift',
      expiresAt: dto.expiresAt
    };
  }

  /**
   * Map PayPeriodStatsDto to PayPeriodStats view model
   */
  mapToPayPeriodStats(dto: PayPeriodStatsDto): PayPeriodStats {
    return {
      hoursWorked: dto.hoursWorked,
      estimatedGrossPay: dto.estimatedGrossPay,
      hoursScheduled: dto.hoursScheduled
    };
  }

  // ============================================================================
  // SHIFT REQUEST ACTIONS
  // ============================================================================

  /**
   * Accept a facility-initiated shift request
   * Delegates to ShiftNeedsService
   * @deprecated Use ShiftNeedsService.acceptShiftRequest() directly
   */
  acceptShiftRequest(
    requestId: string,
    message?: string
  ): Observable<DashboardShiftRequestDto> {
    return this.shiftNeedsService.acceptShiftRequest(requestId, message);
  }

  /**
   * Decline a facility-initiated shift request
   * Delegates to ShiftNeedsService
   * @deprecated Use ShiftNeedsService.declineShiftRequest() directly
   */
  declineShiftRequest(
    requestId: string,
    reason?: string
  ): Observable<{ message: string }> {
    return this.shiftNeedsService.declineShiftRequest(requestId, reason);
  }
}
